import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Search, 
  Refresh,
  Assessment,
  Info,
  CheckCircle,
  Warning,
  Receipt,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Business,
  Description,
  GetApp,
  Print,
  Download
} from '@mui/icons-material';

const ConsolidatedPayoutReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPayout, setSelectedPayout] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Available payout options
  const payoutOptions = [
    'PO001',
    'PO002',
    'PO003',
    'PO004',
    'PO005',
    'PO006',
    'PO007',
    'PO008',
  ];

  // Sample consolidated payout data
  const [consolidatedData] = useState([
    {
      id: 1,
      payout: 'PO001',
      amount: 500000,
      paidAmount: 450000,
      balanceAmount: 50000,
    },
    {
      id: 2,
      payout: 'PO002',
      amount: 350000,
      paidAmount: 350000,
      balanceAmount: 0,
    },
    {
      id: 3,
      payout: 'PO003',
      amount: 750000,
      paidAmount: 600000,
      balanceAmount: 150000,
    },
    {
      id: 4,
      payout: 'PO004',
      amount: 420000,
      paidAmount: 420000,
      balanceAmount: 0,
    },
    {
      id: 5,
      payout: 'PO005',
      amount: 280000,
      paidAmount: 200000,
      balanceAmount: 80000,
    },
    {
      id: 6,
      payout: 'PO006',
      amount: 680000,
      paidAmount: 680000,
      balanceAmount: 0,
    },
    {
      id: 7,
      payout: 'PO007',
      amount: 390000,
      paidAmount: 300000,
      balanceAmount: 90000,
    },
    {
      id: 8,
      payout: 'PO008',
      amount: 560000,
      paidAmount: 560000,
      balanceAmount: 0,
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    console.log('Searching for payout:', selectedPayout);
    setSuccessMessage(`Consolidated payout report generated! Found ${getFilteredData().length} payout record(s).`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleReset = () => {
    setSelectedPayout('');
    setErrors({});
    setSuccessMessage('');
  };

  const getFilteredData = () => {
    let filteredData = consolidatedData;

    // Filter by selected payout
    if (selectedPayout) {
      filteredData = filteredData.filter(item => item.payout === selectedPayout);
    }

    return filteredData;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getSearchSummary = () => {
    const total = getFilteredData().length;
    const totals = calculateTotals();
    return `Showing ${total} consolidated payout record(s) with total amount of ${formatCurrency(totals.amount)}`;
  };

  const getBalanceStatus = (balanceAmount) => {
    if (balanceAmount === 0) {
      return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    } else {
      return { backgroundColor: '#ffebee', color: '#d32f2f' };
    }
  };

  // Calculate totals for filtered data
  const calculateTotals = () => {
    const filteredData = getFilteredData();
    return filteredData.reduce(
      (totals, item) => ({
        amount: totals.amount + item.amount,
        paidAmount: totals.paidAmount + item.paidAmount,
        balanceAmount: totals.balanceAmount + item.balanceAmount,
      }),
      { amount: 0, paidAmount: 0, balanceAmount: 0 }
    );
  };

  const totals = calculateTotals();

  // Summary calculations
  const totalPayouts = consolidatedData.length;
  const fullyPaidCount = consolidatedData.filter(p => p.balanceAmount === 0).length;
  const pendingCount = consolidatedData.filter(p => p.balanceAmount > 0).length;
  const totalAmount = consolidatedData.reduce((sum, payout) => sum + payout.amount, 0);
  const totalPaidAmount = consolidatedData.reduce((sum, payout) => sum + payout.paidAmount, 0);
  const totalBalanceAmount = consolidatedData.reduce((sum, payout) => sum + payout.balanceAmount, 0);

  const filteredData = getFilteredData();
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#536DFE', mb: 0.5 }}>
              Consolidated Payout Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view all consolidated payout reports
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<CheckCircle />}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Consolidated Payout Reports
          </Typography>

          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel>Select Payout</InputLabel>
                <Select
                  value={selectedPayout}
                  label="Select Payout"
                  onChange={(e) => setSelectedPayout(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Receipt sx={{ color: '#666' }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>All Payouts</em>
                  </MenuItem>
                  {payoutOptions.map((payout) => (
                    <MenuItem key={payout} value={payout}>
                      {payout}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Generate Report
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleReset}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>


        
          {/* Results Table */}
          {filteredData.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#536DFE' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        S.No
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Payout Info
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Total Amount
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Paid Amount
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Balance Amount
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((payout, index) => (
                      <TableRow 
                        key={payout.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                          '&:hover': { backgroundColor: '#f0f7ff !important' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Receipt sx={{ fontSize: 16, color: '#1976d2' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                {payout.payout}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#7b1fa2' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(payout.amount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TrendingUp sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                              {formatCurrency(payout.paidAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {payout.balanceAmount === 0 ? (
                              <CheckCircle sx={{ fontSize: 16, color: '#2e7d32' }} />
                            ) : (
                              <TrendingDown sx={{ fontSize: 16, color: '#d32f2f' }} />
                            )}
                            <Chip
                              label={formatCurrency(payout.balanceAmount)}
                              sx={getBalanceStatus(payout.balanceAmount)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Download Report">
                              <IconButton 
                                size="small"
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Print Report">
                              <IconButton 
                                size="small"
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Row */}
              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Assessment sx={{ fontSize: 20, color: '#1976d2' }} />
                          <Typography variant="h6" color="primary.main">
                            GRAND TOTAL
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                          {formatCurrency(totals.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          {formatCurrency(totals.paidAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: totals.balanceAmount > 0 ? '#d32f2f' : '#2e7d32'
                          }}
                        >
                          {formatCurrency(totals.balanceAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ 
                  borderTop: '1px solid #e0e0e0',
                  mt: 2
                }}
              />
            </>
          ) : (
            <Alert 
              severity="warning" 
              icon={<Warning />}
              sx={{ mt: 3 }}
            >
              No consolidated payout records found. Please adjust your search criteria and try again.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConsolidatedPayoutReport;